import React, { useEffect, useState } from "react";
import { Alert, Button, Select, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import {
  fetchClientDailyMeals,
  replaceClientDailyMeals
} from "./nutrition-api";
import { field, numberField, showError } from "./nutrition-utils";
import NutritionDrawer from "./nutrition-drawer";
import "./nutrition.css";

const ClientDailyMealsModal = ({
  open,
  idDoc,
  idStr,
  clientName,
  mealTypeOptions,
  canEdit,
  onClose,
  onSaved
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !idDoc || !idStr) return;
    let cancelled = false;
    setLoading(true);
    fetchClientDailyMeals(idDoc, idStr)
      .then((data) => {
        if (!cancelled) {
          setRows((Array.isArray(data) ? data : []).map((row) => ({
            idNumber: numberField(row, "idNumber"),
            fdate: field(row, "fdate"),
            mealTypeId: numberField(row, "mealTypeId"),
            mealTypeName: field(row, "mealTypeName", ""),
            meal1: numberField(row, "meal1"),
            meal2: numberField(row, "meal2"),
            meal3: numberField(row, "meal3"),
            meal4: numberField(row, "meal4")
          })));
        }
      })
      .catch((e) => !cancelled && showError(messageApi, e, "Не удалось загрузить дневное питание"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, idDoc, idStr, messageApi]);

  const save = async () => {
    setLoading(true);
    try {
      await replaceClientDailyMeals(
        idDoc,
        idStr,
        rows.map((row) => ({
          fdate: dayjs(row.fdate).startOf("day").format("YYYY-MM-DD"),
          mealTypeId: row.mealTypeId
        }))
      );
      messageApi.success("Дневное питание сохранено, путёвка пересчитана");
      onSaved?.();
      onClose();
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить дневное питание");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Дата", width: 110, render: (_, r) => dayjs(r.fdate).format("DD.MM.YYYY") },
    {
      title: "Вид питания",
      render: (_, r, index) => (
        <Select
          value={r.mealTypeId}
          options={[
            { value: 0, label: "По путёвке" },
            ...(mealTypeOptions || []).filter((o) => Number(o?.value) > 0)
          ]}
          disabled={!canEdit}
          style={{ width: "100%" }}
          onChange={(value) => setRows((old) => old.map((x, i) => i === index ? { ...x, mealTypeId: value } : x))}
        />
      )
    },
    ...[
      ["meal1", "Завтрак"],
      ["meal2", "Обед"],
      ["meal3", "Полдник"],
      ["meal4", "Ужин"]
    ].map(([key, title]) => ({
      title,
      width: 90,
      align: "center",
      render: (_, r) => <Tag color={r[key] ? "green" : "default"}>{r[key] ? "Да" : "Нет"}</Tag>
    }))
  ];

  return (
    <NutritionDrawer
      open={open}
      title={`Питание по дням${clientName ? ` — ${clientName}` : ""}`}
      width="min(900px, calc(100vw - 80px))"
      onClose={onClose}
    >
      {contextHolder}
      {!canEdit && <Alert type="info" showIcon message="Просмотр без изменения: требуется право LAW45" style={{ marginBottom: 12 }} />}
      <Table
        className="nutrition-daily-meals"
        rowKey={(r) => `${r.idNumber}:${r.fdate}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 760, y: 480 }}
      />
      <div className="nutrition-drawer-footer">
        {canEdit ? <Button type="primary" loading={loading} onClick={save}>Сохранить</Button> : null}
        <Button onClick={onClose} disabled={loading}>Закрыть</Button>
      </div>
    </NutritionDrawer>
  );
};

export default ClientDailyMealsModal;
